import React, { useState, useRef, useEffect } from 'react';
import { useUnifiedSignalR } from '../../contexts/UnifiedSignalRContext';
import { useHealthStatus } from '../../hooks/useHealthStatus';
import { ServiceHealthStatus } from '../../services/healthService';

export const ConnectionStatus: React.FC = () => {
  const { isConnected, isReconnecting } = useUnifiedSignalR();
  const { status, isRefreshing, refresh } = useHealthStatus(isConnected, isReconnecting);
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPopup]);

  const getStatusInfo = () => {
    if (status.overall === 'disconnected') {
      return {
        color: 'bg-red-500',
        tooltip: 'Disconnected from server',
        showText: true,
        text: 'Disconnected'
      };
    } else if (status.overall === 'degraded' || isReconnecting) {
      return {
        color: 'bg-yellow-500 animate-pulse',
        tooltip: 'Reconnecting to server...',
        showText: true,
        text: 'Reconnecting...'
      };
    } else if (status.overall === 'unhealthy') {
      return {
        color: 'bg-orange-500',
        tooltip: 'Backend services degraded',
        showText: true,
        text: 'Degraded'
      };
    } else {
      return {
        color: 'bg-green-500',
        tooltip: 'Connected to server',
        showText: false
      };
    }
  };

  const statusInfo = getStatusInfo();

  const formatUptime = (uptime: string): string => {
    const seconds = parseInt(uptime.replace('s', ''));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getServiceIcon = (service: ServiceHealthStatus) => {
    switch (service.service.toLowerCase()) {
      case 'chatservice':
        return '💬';
      case 'signalr':
        return '🔌';
      case 'servicebus':
        return '📬';
      case 'cosmosdb':
        return '🗄️';
      case 'aiservice':
        return '🤖';
      default:
        return '⚙️';
    }
  };

  const getServiceStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600';
      case 'unhealthy':
        return 'text-red-600';
      case 'configured':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="relative" ref={popupRef}>
      <button
        onClick={() => setShowPopup(!showPopup)}
        className="flex items-center space-x-2 hover:opacity-80 transition-opacity focus:outline-none cursor-default"
        title={statusInfo.tooltip}
      >
        <div className={`w-2 h-2 rounded-full ${statusInfo.color}`} />
        {statusInfo.showText && (
          <span className="text-xs text-gray-600">{statusInfo.text}</span>
        )}
      </button>

      {showPopup && (
        <div className="absolute left-0 top-0 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${statusInfo.color}`} />
                <h3 className="font-semibold">System Health</h3>
              </div>
              <button
                onClick={refresh}
                disabled={isRefreshing}
                className="text-white hover:bg-white/20 rounded px-2 py-1 text-xs transition-colors disabled:opacity-50"
                title="Refresh health status"
              >
                {isRefreshing ? '⟳' : '↻'}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 max-h-96 overflow-y-auto">
            {/* SignalR Status */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">🔌 SignalR Connection</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  isConnected 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>

            {/* Backend Health */}
            {status.backendHealth ? (
              <>
                {/* Overall Status */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Backend Status</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      status.backendHealth.status === 'healthy'
                        ? 'bg-green-100 text-green-800'
                        : status.backendHealth.status === 'degraded'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {status.backendHealth.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Response: {status.backendHealth.duration}</div>
                    {status.lastCheck && (
                      <div>Last checked: {status.lastCheck.toLocaleTimeString()}</div>
                    )}
                  </div>
                </div>

                {/* Services */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Services</h4>
                  <div className="space-y-2">
                    {status.backendHealth.services.map((service, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{getServiceIcon(service)}</span>
                          <div>
                            <div className="text-xs font-medium text-gray-700">
                              {service.service}
                            </div>
                            {service.type && (
                              <div className="text-xs text-gray-500">
                                {service.type}
                              </div>
                            )}
                            {service.queueName && (
                              <div className="text-xs text-gray-500">
                                Queue: {service.queueName}
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs font-medium ${getServiceStatusColor(service.status)}`}>
                          {service.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-lg font-bold text-gray-700">
                        {status.backendHealth.summary.total}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <div className="text-lg font-bold text-green-700">
                        {status.backendHealth.summary.healthy}
                      </div>
                      <div className="text-xs text-gray-500">Healthy</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2">
                      <div className="text-lg font-bold text-red-700">
                        {status.backendHealth.summary.unhealthy}
                      </div>
                      <div className="text-xs text-gray-500">Issues</div>
                    </div>
                  </div>
                </div>

                {/* Runtime Info */}
                {status.backendHealth.runtime && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Runtime</h4>
                    <div className="bg-gray-50 rounded-lg p-2 space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Uptime:</span>
                        <span className="font-medium">
                          {formatUptime(status.backendHealth.runtime.uptime)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Memory:</span>
                        <span className="font-medium">
                          {status.backendHealth.runtime.memory.used} / {status.backendHealth.runtime.memory.total}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                {isRefreshing ? (
                  <div className="text-gray-500 text-sm">Loading health status...</div>
                ) : isConnected ? (
                  <div className="space-y-2">
                    <div className="text-gray-500 text-sm">Health check pending...</div>
                    <button
                      onClick={refresh}
                      className="text-xs text-blue-600 hover:text-blue-700 underline"
                    >
                      Check now
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">Connect to check health status</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
